import { FormControl } from '@angular/forms';
import { combineLatest, filter, merge, Observable, startWith, tap } from 'rxjs';
import { ValidityUnit } from 'src/app/features/calist/components/create-ca/create-ca.types';

function today(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function expirationDateForDays(validityDays: number): Date {
  const expirationDate = today();
  expirationDate.setDate(expirationDate.getDate() + validityDays);
  return expirationDate;
}

function expirationDateForYears(validityYears: number): Date {
  const expirationDate = today();
  expirationDate.setFullYear(expirationDate.getFullYear() + validityYears);
  return expirationDate;
}

const validityToDateConverters: Record<
  ValidityUnit,
  (validity: number) => Date
> = {
  [ValidityUnit.day]: validity => expirationDateForDays(validity),
  [ValidityUnit.year]: validity => expirationDateForYears(validity),
};

export function validityToExpirationDate(
  validityUnit: ValidityUnit,
  validity: number,
): Date {
  return validityToDateConverters[validityUnit](validity);
}

const msPerDay = 1000 * 60 * 60 * 24;

export function expirationDateToValidityDays(expirationDate: Date): number {
  return Math.round((expirationDate.getTime() - today().getTime()) / msPerDay);
}

export function syncExpirationDateAndDuration({
  validityUnitsControl,
  validityDurationControls,
  expirationDateControl,
  syncIf,
}: {
  validityUnitsControl: FormControl;
  validityDurationControls: Record<ValidityUnit, FormControl>;
  expirationDateControl: FormControl;
  syncIf?: () => boolean;
}): Observable<void> {
  const validityDaysControl = validityDurationControls[ValidityUnit.day];
  const validityYearsControl = validityDurationControls[ValidityUnit.year];
  const updateExpirationDateWhenDurationChanges = combineLatest([
    validityUnitsControl.valueChanges.pipe(
      startWith(validityUnitsControl.value),
    ),
    validityDaysControl.valueChanges.pipe(startWith(validityDaysControl.value)),
    validityYearsControl.valueChanges.pipe(
      startWith(validityYearsControl.value),
    ),
  ]).pipe(
    filter(() => !syncIf || syncIf()),
    tap(([validityUnits, validityDays, validityYears]) => {
      let expirationDate: Date | null = null;
      if (validityUnits === ValidityUnit.day && validityDays) {
        expirationDate = validityToExpirationDate(
          validityUnits,
          parseInt(validityDays),
        );
      }
      if (validityUnits === ValidityUnit.year && validityYears) {
        expirationDate = validityToExpirationDate(
          validityUnits,
          parseInt(validityYears),
        );
      }
      expirationDateControl.setValue(expirationDate, {
        emitEvent: false,
      });
    }),
  );

  const updateDurationWhenExpirationDateChanges =
    expirationDateControl.valueChanges.pipe(
      filter(() => !syncIf || syncIf()),
      tap(value => {
        validityUnitsControl.setValue(ValidityUnit.day);
        validityDaysControl.setValue(
          value ? expirationDateToValidityDays(value).toString() : '',
        );
      }),
    );

  return merge(
    updateExpirationDateWhenDurationChanges,
    updateDurationWhenExpirationDateChanges,
  );
}
